import { useState, useCallback } from 'react';
import { ChatMessage, ChatResponse } from './interface';
import { useContextMetadata } from './getContextMeta';
import { httpConstants } from '../../constants';
import { httpService } from '../../utility/httpService';
import { v4 as uuid } from 'uuid';
import { API_BASE, COPILOT_APP_ID } from '../../config/env';

export const useCopilot = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState(() => uuid());

  const getContextMeta = useContextMetadata();

  const headers = {
    'Content-Type': 'application/json',
  };

  const sendMessageToLLM = useCallback(
    async (content: string): Promise<ChatResponse> => {
      const metadata = getContextMeta();

      if (!metadata?.component || !metadata?.tab || !metadata?.route) {
        throw new Error('Missing required metadata fields: component, tab, or route');
      }

      const requestPayload = {
        appId: COPILOT_APP_ID,
        conversationId,
        messages: [
          {
            role: 'user',
            content,
            metadata: {
              component: metadata.component,
              tab: metadata.tab,
              route: metadata.route,
            },
          },
        ],
      };

      try {
        const response = await httpService<any>(
          httpConstants.METHOD_TYPE.POST,
          `${API_BASE}/api/v3/zbrain/query/`,
          requestPayload,
          headers
        );

        if (!response.responseData) {
          throw new Error('Invalid response structure');
        }

        return {
          content: response.responseData.content,
          command: {
            type: response.responseData?.actionType,
            payload: response.responseData?.actionMetaData,
            actionable: response.responseData?.actionable || false,
          },
        };
      } catch (error: any) {
        throw new Error(error?.message || 'Unexpected error occurred');
      }
    },
    [getContextMeta, conversationId]
  );

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim()) return;

      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        role: 'user',
        content,
        timestamp: new Date(),
        metadata: getContextMeta(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      setError(null);

      try {
        const response = await sendMessageToLLM(content);

        const botMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: 'copilot',
          content: response.content,
          timestamp: new Date(),
          command: response.command,
        };

        setMessages((prev) => [...prev, botMessage]);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
        setError(errorMessage);

        const errorBotMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: 'copilot',
          content: `Sorry, I encountered an error: ${errorMessage}`,
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, errorBotMessage]);
      } finally {
        setIsLoading(false);
      }
    },
    [sendMessageToLLM, getContextMeta]
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
    setConversationId(() => uuid());
  }, []);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages,
  };
};
